import {
  useRef,
  useEffect,
  useState,
  useCallback,
  useLayoutEffect,
} from 'react';
import ReactMarkdown from 'react-markdown';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import HistoryIcon from '@mui/icons-material/History';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import ViewSidebarOutlinedIcon from '@mui/icons-material/ViewSidebarOutlined';
import BorderRightIcon from '@mui/icons-material/BorderRight';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';

export const VIEW_MODE = {
  POPUP: 'popup',
  SIDE: 'side',
  SIDE_OVERLAP: 'side_overlap',
  FULL: 'full',
};

// ── Shared sub-components ────────────────────────────────────────────────────

function TypingDots() {
  return (
    <Box
      sx={{
        display: 'flex',
        gap: '4px',
        alignItems: 'center',
        py: 0.5,
        '& span': {
          width: 7,
          height: 7,
          borderRadius: '50%',
          bgcolor: 'primary.main',
          opacity: 0.4,
          display: 'inline-block',
          animation: 'chatbotBounce 1.2s infinite ease-in-out',
          '&:nth-of-type(1)': { animationDelay: '0s' },
          '&:nth-of-type(2)': { animationDelay: '0.2s' },
          '&:nth-of-type(3)': { animationDelay: '0.4s' },
        },
        '@keyframes chatbotBounce': {
          '0%, 80%, 100%': { transform: 'translateY(0)', opacity: 0.4 },
          '40%': { transform: 'translateY(-6px)', opacity: 1 },
        },
      }}>
      <span />
      <span />
      <span />
    </Box>
  );
}

function MessageRow({ align, children }) {
  const isUser = align === 'right';
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isUser ? 'row-reverse' : 'row',
        alignItems: 'flex-end',
        gap: 1,
        mb: 2,
      }}>
      <Avatar
        sx={{
          width: 28,
          height: 28,
          flexShrink: 0,
          bgcolor: isUser ? 'primary.light' : 'primary.main',
          color: isUser ? 'primary.main' : 'primary.contrastText',
        }}>
        {isUser ? (
          <PersonOutlineIcon sx={{ fontSize: 16 }} />
        ) : (
          <SmartToyOutlinedIcon sx={{ fontSize: 16 }} />
        )}
      </Avatar>
      {children}
    </Box>
  );
}

function UserBubble({ text }) {
  return (
    <MessageRow align="right">
      <Box
        sx={{
          px: 2,
          py: 1.25,
          maxWidth: '78%',
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          borderRadius: '18px 18px 4px 18px',
          boxShadow: '0 2px 8px rgba(0,154,205,0.25)',
        }}>
        <Typography
          variant="body2"
          sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
          {text}
        </Typography>
      </Box>
    </MessageRow>
  );
}

function AdditionalInfoPanel({ additionalInfo }) {
  const [open, setOpen] = useState(false);

  const {
    relatedSnippets = [],
    contextNotes = [],
    sourcesUsed = [],
  } = additionalInfo ?? {};
  const hasContent =
    relatedSnippets.length > 0 ||
    contextNotes.length > 0 ||
    sourcesUsed.length > 0;

  if (!hasContent) return null;

  return (
    <Box sx={{ mt: 0.75 }}>
      <Button
        size="small"
        startIcon={<InfoOutlinedIcon sx={{ fontSize: '0.8rem' }} />}
        endIcon={
          open ? (
            <KeyboardArrowUpIcon sx={{ fontSize: '0.85rem' }} />
          ) : (
            <KeyboardArrowDownIcon sx={{ fontSize: '0.85rem' }} />
          )
        }
        onClick={() => setOpen(v => !v)}
        sx={{
          textTransform: 'none',
          fontSize: '0.7rem',
          color: 'text.secondary',
          p: '2px 8px',
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'grey.200',
          bgcolor: '#f8f9fb',
          '&:hover': { bgcolor: 'grey.100' },
        }}>
        Additional info
      </Button>

      <Collapse in={open}>
        <Box
          sx={{
            mt: 0.75,
            px: 1.5,
            py: 1.25,
            bgcolor: '#f8f9fb',
            border: '1px solid',
            borderColor: 'grey.200',
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.25,
          }}>
          {relatedSnippets.length > 0 && (
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  mb: 0.5,
                }}>
                <ArticleOutlinedIcon
                  sx={{ fontSize: '0.75rem', color: 'primary.main' }}
                />
                <Typography
                  variant="caption"
                  fontWeight={600}
                  color="primary.main"
                  sx={{
                    fontSize: '0.68rem',
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                  }}>
                  Related excerpts
                </Typography>
              </Box>
              {relatedSnippets.map(snippet => (
                <Box
                  key={snippet}
                  sx={{
                    px: 1.25,
                    py: 0.75,
                    mb: 0.5,
                    bgcolor: 'white',
                    border: '1px solid',
                    borderColor: 'grey.200',
                    borderLeft: '3px solid',
                    borderLeftColor: 'primary.light',
                    borderRadius: 1,
                  }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ lineHeight: 1.5, fontSize: '0.72rem' }}>
                    {snippet}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {contextNotes.length > 0 && (
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  mb: 0.5,
                }}>
                <WarningAmberOutlinedIcon
                  sx={{ fontSize: '0.75rem', color: 'warning.main' }}
                />
                <Typography
                  variant="caption"
                  fontWeight={600}
                  color="warning.main"
                  sx={{
                    fontSize: '0.68rem',
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                  }}>
                  Notes & caveats
                </Typography>
              </Box>
              {contextNotes.map(note => (
                <Box
                  key={note}
                  sx={{
                    px: 1.25,
                    py: 0.75,
                    mb: 0.5,
                    bgcolor: '#fffbf0',
                    border: '1px solid',
                    borderColor: 'warning.light',
                    borderRadius: 1,
                  }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ lineHeight: 1.5, fontSize: '0.72rem' }}>
                    {note}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {sourcesUsed.length > 0 && (
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  mb: 0.5,
                }}>
                <LayersOutlinedIcon
                  sx={{ fontSize: '0.75rem', color: 'text.secondary' }}
                />
                <Typography
                  variant="caption"
                  fontWeight={600}
                  color="text.secondary"
                  sx={{
                    fontSize: '0.68rem',
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                  }}>
                  Sources
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {sourcesUsed.map(src => (
                  <Chip
                    key={src}
                    label={src}
                    size="small"
                    sx={{
                      fontSize: '0.65rem',
                      height: 20,
                      bgcolor: 'white',
                      border: '1px solid',
                      borderColor: 'grey.300',
                      color: 'text.secondary',
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

function MessageFeedback({
  messageKey,
  messageId,
  messageIndex,
  feedback,
  onSubmitFeedback,
  feedbackReason,
  onFeedbackReasonChange,
  expandedNotHelpful,
  onExpandNotHelpful,
  submittingFeedback,
}) {
  const showReason =
    expandedNotHelpful[messageKey] && feedback !== 'not_helpful';
  const reason = feedbackReason[messageKey] ?? '';

  return (
    <Box sx={{ mt: 0.75 }}>
      <Typography
        component="span"
        variant="caption"
        sx={{ fontSize: '0.7rem', color: 'text.secondary', mr: 0.75 }}>
        Was this helpful?
      </Typography>
      <Box
        component="span"
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
        <Tooltip title="Helpful">
          <IconButton
            size="small"
            onClick={() =>
              onSubmitFeedback(
                messageKey,
                messageId,
                true,
                undefined,
                messageIndex,
              )
            }
            disabled={submittingFeedback === messageKey}
            sx={{
              p: 0.25,
              color:
                feedback === 'helpful' ? 'primary.main' : 'action.disabled',
              '&:hover': { color: 'primary.main', bgcolor: 'action.hover' },
            }}
            aria-pressed={feedback === 'helpful'}>
            <ThumbUpOutlinedIcon sx={{ fontSize: '1rem' }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Not helpful">
          <IconButton
            size="small"
            onClick={() => {
              if (feedback === 'not_helpful') return;
              onExpandNotHelpful(prev => ({ ...prev, [messageKey]: true }));
            }}
            disabled={submittingFeedback === messageKey}
            sx={{
              p: 0.25,
              color:
                feedback === 'not_helpful' ? 'error.main' : 'action.disabled',
              '&:hover': { color: 'error.main', bgcolor: 'action.hover' },
            }}
            aria-pressed={feedback === 'not_helpful'}>
            <ThumbDownOutlinedIcon sx={{ fontSize: '1rem' }} />
          </IconButton>
        </Tooltip>
      </Box>
      {showReason && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            mt: 0.75,
            flexWrap: 'wrap',
          }}>
          <TextField
            size="small"
            placeholder="Reason (optional)"
            value={reason}
            onChange={e =>
              onFeedbackReasonChange(prev => ({
                ...prev,
                [messageKey]: e.target.value,
              }))
            }
            sx={{
              flex: '1 1 120px',
              minWidth: 120,
              '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.5 },
            }}
          />
          <Button
            size="small"
            onClick={() =>
              onSubmitFeedback(
                messageKey,
                messageId,
                false,
                reason || undefined,
                messageIndex,
              )
            }
            disabled={submittingFeedback === messageKey}
            sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
            {submittingFeedback === messageKey ? '…' : 'Submit'}
          </Button>
          <Button
            size="small"
            onClick={() =>
              onExpandNotHelpful(prev => ({ ...prev, [messageKey]: false }))
            }
            sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
            Cancel
          </Button>
        </Box>
      )}
    </Box>
  );
}

function AssistantBubble({
  text,
  isError,
  onRetry,
  isLast,
  additionalInfo,
  messageKey,
  messageId,
  messageIndex,
  feedback,
  onFeedback,
  feedbackReason,
  setFeedbackReason,
  expandedNotHelpful,
  setExpandedNotHelpful,
  submittingFeedback,
}) {
  return (
    <MessageRow align="left">
      <Box sx={{ maxWidth: '82%' }}>
        <Box
          sx={{
            px: 2,
            py: 1.25,
            bgcolor: isError ? '#fff0f0' : '#ffffff',
            border: '1px solid',
            borderColor: isError ? 'error.light' : 'grey.200',
            borderRadius: '18px 18px 18px 4px',
            boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
            '& p': { margin: 0, lineHeight: 1.6 },
            '& p + p': { mt: 1 },
            '& pre': {
              overflowX: 'auto',
              bgcolor: '#f4f6f8',
              border: '1px solid',
              borderColor: 'grey.200',
              p: 1.5,
              borderRadius: 1.5,
              fontSize: '0.75rem',
              mt: 1,
            },
            '& code': {
              fontSize: '0.78rem',
              bgcolor: '#f4f6f8',
              px: 0.5,
              borderRadius: 0.5,
            },
            '& ul, & ol': { pl: 2.5, my: 0.5 },
            '& li': { mb: 0.25 },
            '& h3': { fontSize: '0.85rem', fontWeight: 600, mt: 1.5, mb: 0.5 },
            '& strong': { fontWeight: 600 },
          }}>
          <Typography
            variant="body2"
            component="div"
            color={isError ? 'error.main' : 'text.primary'}>
            <ReactMarkdown>{text}</ReactMarkdown>
          </Typography>
        </Box>

        {!isError && <AdditionalInfoPanel additionalInfo={additionalInfo} />}

        {!isError && (
          <MessageFeedback
            messageKey={messageKey}
            messageId={messageId}
            messageIndex={messageIndex}
            feedback={feedback}
            onSubmitFeedback={onFeedback}
            feedbackReason={feedbackReason}
            onFeedbackReasonChange={setFeedbackReason}
            expandedNotHelpful={expandedNotHelpful}
            onExpandNotHelpful={setExpandedNotHelpful}
            submittingFeedback={submittingFeedback}
          />
        )}

        {isError && isLast && (
          <Button
            size="small"
            startIcon={<RefreshIcon sx={{ fontSize: '0.85rem' }} />}
            onClick={onRetry}
            sx={{
              mt: 0.75,
              ml: 0.5,
              textTransform: 'none',
              fontSize: '0.75rem',
              color: 'error.main',
              p: '2px 8px',
              borderRadius: 2,
              '&:hover': { bgcolor: '#fff0f0' },
            }}
            variant="text">
            Try again
          </Button>
        )}
      </Box>
    </MessageRow>
  );
}

function TypingRow() {
  return (
    <MessageRow align="left">
      <Box
        sx={{
          px: 2,
          py: 1.25,
          bgcolor: '#ffffff',
          border: '1px solid',
          borderColor: 'grey.200',
          borderRadius: '18px 18px 18px 4px',
          boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        }}>
        <TypingDots />
      </Box>
    </MessageRow>
  );
}

const SUGGESTIONS = [
  'How do I create a claim?',
  'What are the claim statuses?',
  'How do I dispatch?',
  'How do I create a new service job?',
  'What does Pending Administrator Review mean?',
];

const ANIM_DURATION = 340; // ms

function hiddenTransform(viewMode) {
  if (viewMode === VIEW_MODE.POPUP) return 'scale(0)';
  if (viewMode === VIEW_MODE.SIDE || viewMode === VIEW_MODE.SIDE_OVERLAP)
    return 'translateX(100%)';
  return 'scale(0.98)'; // fullscreen
}

// ── Main ChatWindow ──────────────────────────────────────────────────────────

function ChatWindow({
  isOpen,
  viewMode,
  onChangeViewMode,
  onClose,
  messages,
  isLoading,
  onSend,
  onRetry,
  sessions = [],
  sessionsLoading = false,
  loadingSession = false,
  onRefetchSessions,
  onLoadSession,
  onStartNewChat,
  onFeedback,
  feedbackReason = {},
  setFeedbackReason,
  expandedNotHelpful = {},
  setExpandedNotHelpful,
  submittingFeedback = null,
}) {
  const [input, setInput] = useState('');
  const messagesEndRef = useRef(null);
  const [historyAnchor, setHistoryAnchor] = useState(null);

  const handleHistoryClick = useCallback(
    e => {
      if (onRefetchSessions) onRefetchSessions();
      setHistoryAnchor(e.currentTarget);
    },
    [onRefetchSessions],
  );

  const lastAssistantSuggestions = (() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i].role === 'assistant' && !messages[i].isError) {
        return messages[i].suggestions ?? [];
      }
    }
    return [];
  })();

  // ── Enter / exit animation state ──────────────────────────────────────────
  // `mounted` keeps the DOM node alive during the exit transition
  const [mounted, setMounted] = useState(isOpen);
  // `visible` drives the CSS opacity/transform — flips after mount (enter) or before unmount (exit)
  const [visible, setVisible] = useState(false);

  useLayoutEffect(() => {
    if (isOpen) {
      setMounted(true);
      // Two-frame defer: first frame applies scale(0) starting state, second frame triggers the expand.
      // The ~60ms window lets the FAB Zoom-exit overlap with the popup's entry for a continuous feel.
      let raf2;
      const raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => setVisible(true));
      });
      return () => {
        cancelAnimationFrame(raf1);
        cancelAnimationFrame(raf2);
      };
    }
    setVisible(false);
    const t = setTimeout(() => setMounted(false), ANIM_DURATION);
    return () => clearTimeout(t);
  }, [isOpen]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  const handleSend = useCallback(
    text => {
      const trimmed = (text ?? input).trim();
      if (!trimmed) return;
      setInput('');
      onSend(trimmed);
    },
    [input, onSend],
  );

  const handleKeyDown = useCallback(
    e => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  if (!mounted) return null;

  // ── Position / size per view mode ─────────────────────────────────────────
  const isPopup = viewMode === VIEW_MODE.POPUP;
  const isFull = viewMode === VIEW_MODE.FULL;
  const isSide = viewMode === VIEW_MODE.SIDE;
  const isSideOverlap = viewMode === VIEW_MODE.SIDE_OVERLAP;

  const containerSx = (() => {
    const base = {
      display: 'flex',
      flexDirection: 'column',
      bgcolor: 'white',
      overflow: 'hidden',
      zIndex: theme => theme.zIndex.drawer + 10,
      opacity: visible ? 1 : 0,
      transform: visible ? 'none' : hiddenTransform(viewMode),
      transition: `opacity ${ANIM_DURATION}ms cubic-bezier(0.34,1.56,0.64,1), transform ${ANIM_DURATION}ms cubic-bezier(0.34,1.56,0.64,1)`,
    };

    if (isPopup) {
      return {
        ...base,
        position: 'fixed',
        bottom: 28,
        right: 28,
        width: 380,
        height: 520,
        // Morphs from pill shape (FAB) to card shape on open, reverses on close
        borderRadius: visible ? '12px' : '28px',
        transition: `opacity ${ANIM_DURATION}ms cubic-bezier(0.34,1.56,0.64,1), transform ${ANIM_DURATION}ms cubic-bezier(0.34,1.56,0.64,1), border-radius ${ANIM_DURATION}ms cubic-bezier(0.34,1.56,0.64,1)`,
        boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
        border: '1px solid',
        borderColor: 'grey.200',
        // Scale origin anchored to the FAB's corner so the expansion feels like it comes from the button
        transformOrigin: 'bottom right',
      };
    }
    if (isFull) {
      return {
        ...base,
        position: 'fixed',
        inset: 0,
        borderRadius: 0,
        boxShadow: 'none',
        transformOrigin: 'center',
      };
    }
    if (isSideOverlap) {
      // 25% overlay — floats over content without shifting the page layout
      return {
        ...base,
        position: 'fixed',
        top: 0,
        right: 0,
        width: '25%',
        minWidth: 280,
        height: '100%',
        borderRadius: 0,
        boxShadow: '-4px 0 24px rgba(0,0,0,0.14)',
        transformOrigin: 'right center',
      };
    }
    // side panel — pushes page content (handled by ProtectedRoute margin)
    return {
      ...base,
      position: 'fixed',
      top: 0,
      right: 0,
      width: 440,
      height: '100%',
      borderRadius: 0,
      boxShadow: '-4px 0 24px rgba(0,0,0,0.10)',
      transformOrigin: 'right center',
    };
  })();

  return (
    <Box sx={containerSx}>
      {/* ── Header ── */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          background: 'linear-gradient(135deg, #009ACD 0%, #0076a7 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexShrink: 0,
        }}>
        <Avatar
          sx={{
            bgcolor: 'rgba(255,255,255,0.2)',
            width: 34,
            height: 34,
          }}>
          <AutoAwesomeIcon sx={{ fontSize: 18 }} />
        </Avatar>

        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="subtitle2" fontWeight={700} lineHeight={1.2}>
            AI Assistant
          </Typography>
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.2 }}>
            <FiberManualRecordIcon sx={{ fontSize: 8, color: '#69f0ae' }} />
            <Typography
              variant="caption"
              sx={{ opacity: 0.85, fontSize: '0.68rem' }}>
              Online
            </Typography>
          </Box>
        </Box>

        {/* New chat & History */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
          <Tooltip title="New chat">
            <IconButton
              size="small"
              onClick={onStartNewChat}
              sx={{
                color: 'white',
                bgcolor: 'rgba(255,255,255,0.12)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
              }}>
              <AddIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Chat history">
            <IconButton
              size="small"
              onClick={handleHistoryClick}
              disabled={sessionsLoading}
              sx={{
                color: 'white',
                bgcolor: 'rgba(255,255,255,0.12)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
                '&.Mui-disabled': { color: 'rgba(255,255,255,0.5)' },
              }}>
              <HistoryIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={historyAnchor}
            open={Boolean(historyAnchor)}
            onClose={() => setHistoryAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{ paper: { sx: { mt: 1.5, minWidth: 220 } } }}>
            {sessions.length === 0 && !sessionsLoading ? (
              <MenuItem disabled>
                <ListItemText primary="No chat history" />
              </MenuItem>
            ) : (
              sessions.map(s => (
                <MenuItem
                  key={s.id}
                  onClick={() => {
                    if (onLoadSession) onLoadSession(s.id);
                    setHistoryAnchor(null);
                  }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <HistoryIcon sx={{ fontSize: 18 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Chat Session"
                    secondary={
                      s.created_at
                        ? new Date(s.created_at).toLocaleDateString(undefined, {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                        : null
                    }
                  />
                </MenuItem>
              ))
            )}
          </Menu>
        </Box>

        {/* View-mode toggles */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {!isPopup && (
            <Tooltip title="Compact view">
              <IconButton
                size="small"
                onClick={() => onChangeViewMode(VIEW_MODE.POPUP)}
                sx={{
                  color: 'white',
                  bgcolor: isPopup
                    ? 'rgba(255,255,255,0.3)'
                    : 'rgba(255,255,255,0.12)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
                }}>
                <CloseFullscreenIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          )}
          {!isSide && (
            <Tooltip title="Side panel">
              <IconButton
                size="small"
                onClick={() => onChangeViewMode(VIEW_MODE.SIDE)}
                sx={{
                  color: 'white',
                  bgcolor: isSide
                    ? 'rgba(255,255,255,0.3)'
                    : 'rgba(255,255,255,0.12)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
                }}>
                <ViewSidebarOutlinedIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          )}
          {!isSideOverlap && (
            <Tooltip title="Overlay panel (25%)">
              <IconButton
                size="small"
                onClick={() => onChangeViewMode(VIEW_MODE.SIDE_OVERLAP)}
                sx={{
                  color: 'white',
                  bgcolor: isSideOverlap
                    ? 'rgba(255,255,255,0.3)'
                    : 'rgba(255,255,255,0.12)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
                }}>
                <BorderRightIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          )}
          {!isFull && (
            <Tooltip title="Full screen">
              <IconButton
                size="small"
                onClick={() => onChangeViewMode(VIEW_MODE.FULL)}
                sx={{
                  color: 'white',
                  bgcolor: isFull
                    ? 'rgba(255,255,255,0.3)'
                    : 'rgba(255,255,255,0.12)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
                }}>
                <OpenInFullIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Close">
            <IconButton
              size="small"
              onClick={onClose}
              sx={{
                color: 'white',
                bgcolor: 'rgba(255,255,255,0.12)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
              }}>
              <CloseIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* ── Messages ── */}
      <Box
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          px: isFull ? 6 : 2,
          py: 2.5,
          bgcolor: '#f8f9fb',
          scrollbarWidth: 'thin',
          scrollbarColor: '#d0d0d0 transparent',
        }}>
        {/* Empty state */}
        {messages.length === 0 && (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              px: 3,
            }}>
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                bgcolor: 'primary.light',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <AutoAwesomeIcon sx={{ fontSize: 36, color: 'primary.main' }} />
            </Box>
            <Typography variant="subtitle2" fontWeight={600} textAlign="center">
              How can I help you?
            </Typography>
            <Typography
              variant="caption"
              textAlign="center"
              color="text.secondary"
              lineHeight={1.6}>
              Ask me anything about your documents. I will find the most
              relevant answers.
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                justifyContent: 'center',
                mt: 1,
              }}>
              {SUGGESTIONS.map(s => (
                <Chip
                  key={s}
                  label={s}
                  size="small"
                  onClick={() => handleSend(s)}
                  sx={{
                    fontSize: '0.72rem',
                    cursor: 'pointer',
                    bgcolor: 'white',
                    border: '1px solid',
                    borderColor: 'primary.light',
                    color: 'primary.main',
                    '&:hover': { bgcolor: 'primary.light' },
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Message list */}
        <Box sx={{ maxWidth: isFull ? 800 : 'none', mx: 'auto' }}>
          {messages.map((msg, idx) =>
            msg.role === 'user' ? (
              <UserBubble key={msg.id} text={msg.text} />
            ) : (
              <AssistantBubble
                key={msg.id}
                text={msg.text}
                isError={msg.isError}
                isLast={idx === messages.length - 1}
                onRetry={onRetry}
                additionalInfo={msg.additionalInfo}
                messageKey={msg.id}
                messageId={msg.messageId}
                messageIndex={idx}
                feedback={msg.feedback}
                onFeedback={onFeedback}
                feedbackReason={feedbackReason}
                setFeedbackReason={setFeedbackReason}
                expandedNotHelpful={expandedNotHelpful}
                setExpandedNotHelpful={setExpandedNotHelpful}
                submittingFeedback={submittingFeedback}
              />
            ),
          )}
          {(isLoading || loadingSession) && <TypingRow />}
          <div ref={messagesEndRef} />
        </Box>
      </Box>

      <Divider />

      {/* ── Suggestion pills ── */}
      {!isLoading && lastAssistantSuggestions.length > 0 && (
        <Box
          sx={{
            px: isFull ? 6 : 2,
            pt: 1.25,
            pb: 0.5,
            bgcolor: 'white',
            flexShrink: 0,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.75,
          }}>
          {lastAssistantSuggestions.map(s => {
            const label = s?.label ?? s;
            const question = s?.question ?? s;
            return (
              <Chip
                key={question}
                label={label}
                size="small"
                onClick={() => handleSend(question)}
                sx={{
                  fontSize: '0.72rem',
                  cursor: 'pointer',
                  bgcolor: 'white',
                  border: '1px solid',
                  borderColor: 'primary.light',
                  color: 'primary.main',
                  '&:hover': { bgcolor: 'primary.light' },
                }}
              />
            );
          })}
        </Box>
      )}

      {/* ── Input ── */}
      <Box
        sx={{
          px: isFull ? 6 : 2,
          py: 1.75,
          bgcolor: 'white',
          flexShrink: 0,
        }}>
        <Box sx={{ maxWidth: isFull ? 800 : 'none', mx: 'auto' }}>
          <TextField
            fullWidth
            size="small"
            multiline
            maxRows={4}
            placeholder="Ask a question… (Enter to send)"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isLoading}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="Send (Enter)">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => handleSend()}
                        disabled={isLoading || !input.trim()}
                        sx={{
                          bgcolor:
                            input.trim() && !isLoading
                              ? 'primary.main'
                              : 'transparent',
                          color:
                            input.trim() && !isLoading
                              ? 'white'
                              : 'action.disabled',
                          width: 32,
                          height: 32,
                          transition: 'all 0.2s',
                          '&:hover': {
                            bgcolor:
                              input.trim() && !isLoading
                                ? 'primary.dark'
                                : 'transparent',
                          },
                        }}>
                        <SendRoundedIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                bgcolor: '#f8f9fb',
                fontSize: '0.875rem',
                height: 'auto',
                '&:hover fieldset': { borderColor: 'primary.light' },
                '&.Mui-focused fieldset': {
                  borderColor: 'primary.main',
                  borderWidth: 1.5,
                },
              },
            }}
          />
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{
              display: 'block',
              textAlign: 'right',
              mt: 0.5,
              fontSize: '0.65rem',
            }}>
            Shift + Enter for new line
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export default ChatWindow;
